import { Array, Boolean, Number, Object, Optional, Static, String, Union, Null } from '@sinclair/typebox';

import { AwardBaggageAllowance } from '../../..';
import { paxType } from '../../schemas/sas-domain/pax';

/* eslint-disable no-useless-escape */
const datePattern = `(20[1-9][1-9])-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])`;
const timePattern = `([01]\d|2[0-3]):[0-5]\d`;
const dateTimePattern = `${datePattern} ${timePattern}`;
/* eslint-enable no-useless-escape */

export namespace SkyAwardEmailNS {
  export type Ancillary = Static<typeof Ancillary>;
  export type BoundSummary = Static<typeof BoundSummary>;
  export type Segment = Static<typeof Segment>;
  export type Passenger = Static<typeof Passenger>;
  export type PaymentItem = Static<typeof PaymentItem>;
  export type Payment = Static<typeof Payment>;
  export type Link = Static<typeof Link>;
  export type Content = Static<typeof Content>;

  export type Payload = Static<typeof Payload>;
  export type StringifiedPayload = Payload & {
    outboundSummary: string;
    outbound: string;
    inboundSummary?: string;
    inbound?: string;
    passengers: string;
    payment: string;
    links: string;
    content?: string;
  };
}

const Ancillary = Object({
  code: String(),
  text: String(),
  status: String(),
});

const BoundSummary = Object({
  ticketType: String(),
  travelClass: String(),
  duration: String(),
  numberOfStops: Number({
    description: 'Use 0 for a direct flight',
  }),
  startDateLocal: String({
    pattern: `^${dateTimePattern}$`,
    description: 'Must match pattern: yyyy-MM-dd hh:mm (2024-01-01 17:00)',
  }),
  startTimeLocal: String({
    pattern: `^${timePattern}$`,
    description: 'Must match pattern: hh:mm (17:00)',
  }),
  endDateLocal: String({
    pattern: `^${datePattern}$`,
    description: 'Must match pattern: yyyy-MM-dd (2024-01-01)',
  }),
  endTimeLocal: String({
    pattern: `^${timePattern}$`,
    description: 'Must match pattern: hh:mm (17:00)',
  }),
  originCountryCode: String(),
  originCityName: String(),
  originAirportCode: String(),
  destinationCountryCode: String(),
  destinationCityName: String(),
  destinationAirportCode: String(),
  ancillaries: Array(Ancillary),
});

const Segment = Object({
  duration: String(),
  transitTime: Union([String(), Null()]),
  departureDateLocal: String({
    pattern: `^${datePattern}$`,
    description: 'Must match pattern: yyyy-MM-dd (2024-01-01)',
  }),
  departureTimeLocal: String({
    pattern: `^${timePattern}$`,
    description: 'Must match pattern: hh:mm (17:00)',
  }),
  arrivalDateLocal: String({
    pattern: `^${datePattern}$`,
    description: 'Must match pattern: yyyy-MM-dd (2024-01-01)',
  }),
  arrivalTimeLocal: String({
    pattern: `^${timePattern}$`,
    description: 'Must match pattern: hh:mm (17:00)',
  }),
  departureCityName: String(),
  departureAirportName: String(),
  departureAirportCode: String(),
  departureTerminal: Optional(String()),
  arrivalCityName: String(),
  arrivalAirportName: String(),
  arrivalAirportCode: String(),
  arrivalTerminal: Optional(String()),
  operatingCarrier: String(),
  flightNumber: String(),
  bookingClass: String(),
  ancillaries: Array(Ancillary),
});

const Passenger = Object({
  id: String(),
  type: paxType,
  firstName: Optional(String()),
  lastName: Optional(String()),
  email: Optional(String()),
  mobile: Optional(String()),
  phone: Optional(String()),
  frequentFlyerNumber: Optional(String()),
  isPrimary: Boolean(),
  hasInfant: Boolean(),
});

const PaymentItem = Object({
  code: String(),
  text: String(),
  amount: String(),
});

const Payment = Object({
  totalPrice: String(),
  totalPoints: String(),
  items: Array(PaymentItem),
});

const Link = Object({
  code: String(),
  url: String(),
});

const Content = Object({
  displayCarRental: Boolean(),
  displayHotel: Boolean(),
});

export const Payload = Object({
  // messageKey: String(),
  correlationId: String(),
  pos: String({ pattern: '[A-Z]{2}' }),
  type: String(),
  pnr: String(),
  countryCode: String({ pattern: '[A-Z]{2}' }),
  languageCode: String({ pattern: '[A-Z]{2}' }),
  isShortHaul: Boolean(),
  isOneway: Boolean(),
  requestDateUTC: String({
    pattern: `^${dateTimePattern}$`,
    description: 'Must match pattern: yyyy-MM-dd hh:mm (2024-01-01 17:00)',
  }),
  outboundSummary: BoundSummary,
  outbound: Object({
    segments: Array(Segment),
  }),
  inboundSummary: Optional(BoundSummary),
  inbound: Optional(
    Object({
      segments: Array(Segment),
    })
  ),
  passengers: Array(Passenger),
  baggageAllowance: Optional(AwardBaggageAllowance),
  payment: Payment,
  links: Array(Link),
  content: Optional(Content),
});
